import { currentTheme } from 'web-app/styleguide';
/**
 * Matches a single, injection-safe CSS color token — a hex color (`#rgb`,
 * `#rgba`, `#rrggbb`, `#rrggbbaa`), a functional `rgb()/rgba()/hsl()/hsla()`
 * color, or a plain named color / CSS-wide keyword (letters only, e.g. `red`,
 * `transparent`, `currentColor`).
 *
 * The `^…$` anchoring is the safeguard: a value can only match if the **entire**
 * string is one colour token, so anything containing `;`, `{`, `}`, `url(...)`,
 * or other characters needed to break out of a CSS declaration fails to match.
 */
const SAFE_CSS_COLOR = /^(#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|(?:rgb|rgba|hsl|hsla)\([\d.,%\s/]*\)|[a-zA-Z]+)$/;
/**
 * Resolves a color value that may be either a legacy theme-palette key
 * (e.g. `'accent1'`) or a raw CSS color value (e.g. a hex such as `'#319ADE'`
 * produced by the Modern Famly color picker).
 *
 * Theme keys are looked up in the given theme. A value that is not a theme key
 * is only returned when it is a valid, injection-safe CSS color token; anything
 * else falls back to a safe neutral color. This is required because the returned
 * value is interpolated raw into `styled-components` (compiled through stylis),
 * so passing an unvalidated value through would allow CSS injection (e.g. a
 * stored value like `red; } body { background: url(https://evil.example) } .x {`
 * could break out of the declaration).
 *
 * @param color - A theme-palette key or a raw CSS color value.
 * @param theme - The theme to resolve palette keys against. Defaults to the current theme.
 * @returns The resolved CSS color string, or a safe neutral fallback for invalid input.
 *
 * @example
 * resolveThemeColor('accent1'); // -> theme.accent1 (e.g. '#...')
 * resolveThemeColor('#319ADE'); // -> '#319ADE'
 * resolveThemeColor('red; } body {}'); // -> theme.textSecondary (rejected)
 */
export const resolveThemeColor = (color, theme = currentTheme()) => {
    const themeValue = theme[color];
    if (typeof themeValue === 'string') {
        return themeValue;
    }
    return SAFE_CSS_COLOR.test(color) ? color : theme.textSecondary;
};
export const getThemeColor = (color, secondary = false, disabled = false, theme = currentTheme()) => {
    switch (color) {
        case 'text': {
            if (disabled) {
                return theme.textDisabled;
            }
            else if (secondary) {
                return theme.textSecondary;
            }
            else {
                return theme.text;
            }
        }
        case 'invertedText': {
            if (disabled) {
                return theme.invertedTextDisabled;
            }
            else if (secondary) {
                return theme.invertedTextSecondary;
            }
            else {
                return theme.invertedText;
            }
        }
        default:
            return theme[color];
    }
};
